import { useMemo, useState } from "react";
import {
  TrendingUp, CircleDollarSign, AlertCircle, RefreshCw,
  Home, Banknote, FileX, Gauge,
} from "lucide-react";
import { FilterBar }    from "@/components/ui/FilterBar";
import { Card }         from "@/components/ui/Card";
import { Button }       from "@/components/ui/Button";
import { useModules }   from "@/context/ModulesContext";
import { IncomeItem }   from "@/services/financeiro";
import { formatCurrency, formatNumber } from "@/utils/format";

import {
  computeReceitasPorMes,
  computeMediaCasasVendidas,
  computeEvolucaoVendas,
  computeRankingBrokers,
  countCasasVendidas,
  countDistratos,
  computeCasasVendidasDetalhe,
  computeDistratosDetalhe,
  computeVelocidadeVendasMensal,
  excludeVendasDistratadas,
} from "./chartUtils";
import { CasasVendidasModal } from "./CasasVendidasModal";
import { DistratosModal } from "./DistratosModal";
import { VelocidadeVendasModal } from "./VelocidadeVendasModal";
import { VendedoresRankingModal } from "./VendedoresRankingModal";

import { ReceitasPorMes }      from "./charts/ReceitasPorMes";
import { EvolucaoVendas }      from "./charts/ValorMedioPorCentro";
import { RankingClientes }     from "./charts/RankingClientes";

/* ─── KPIs ───────────────────────────────────────────────────────────────── */
function computeKpis(income: IncomeItem[]) {
  // Preço Geral / Preço Médio Geral: soma vendas excluindo condição DE (desconto)
  const totalSemDesconto = income.reduce(
    (s, i) => s + i.originalAmount - (i.discountAmount ?? 0),
    0,
  );
  const preChave = income.reduce((s, i) => s + (i.preChaveAmount ?? 0), 0);
  return { totalSemDesconto, preChave };
}

/* ─── Página ─────────────────────────────────────────────────────────────── */
export function ComercialPage() {
  const { comercial, ccOptions, loadingCC } = useModules();
  const { values, setValues, apply, reset, data, loading, error, applied } = comercial;

  const income = data?.income ?? [];
  const salesHistory = data?.salesHistory ?? [];
  const distratosList = data?.distratos ?? [];
  const distratosAtePesquisa = data?.distratosAtePesquisa ?? [];
  const units  = data?.units  ?? [];
  const isFirstLoad = loading && data == null;
  const awaitingApply = data == null && !loading;

  const incomeValido = useMemo(
    () => excludeVendasDistratadas(income, distratosAtePesquisa, applied.endDate),
    [income, distratosAtePesquisa, applied.endDate],
  );
  const kpis = data ? computeKpis(incomeValido) : null;

  const receitasPorMes = useMemo(() => computeReceitasPorMes(incomeValido), [incomeValido]);
  const velocidadeVendas = useMemo(
    () => computeMediaCasasVendidas(salesHistory, units, applied.endDate),
    [salesHistory, units, applied.endDate],
  );
  const evolucaoVendas = useMemo(
    () => computeEvolucaoVendas(incomeValido, distratosList, units.length, income),
    [incomeValido, distratosList, units.length, income],
  );
  const brokers        = useMemo(() => computeRankingBrokers(incomeValido), [incomeValido]);
  const allBrokers     = useMemo(
    () => computeRankingBrokers(incomeValido, Number.POSITIVE_INFINITY),
    [incomeValido],
  );
  const casasVendidas  = useMemo(() => countCasasVendidas(incomeValido), [incomeValido]);
  const distratosCount = useMemo(() => countDistratos(distratosList),      [distratosList]);
  const casasDetalhe   = useMemo(
    () => computeCasasVendidasDetalhe(incomeValido, ccOptions, units),
    [incomeValido, ccOptions, units],
  );
  const distratosDetalhe = useMemo(
    () => computeDistratosDetalhe(distratosList, ccOptions, units),
    [distratosList, ccOptions, units],
  );
  const velocidadeMensal = useMemo(
    () => computeVelocidadeVendasMensal(
      salesHistory,
      distratosAtePesquisa,
      units,
      applied.endDate,
    ),
    [salesHistory, distratosAtePesquisa, units, applied.endDate],
  );
  const [casasModalOpen, setCasasModalOpen] = useState(false);
  const [distratosModalOpen, setDistratosModalOpen] = useState(false);
  const [velocidadeModalOpen, setVelocidadeModalOpen] = useState(false);
  const [vendedoresModalOpen, setVendedoresModalOpen] = useState(false);
  const precoMedioGeral =
    kpis && casasVendidas.count > 0 ? kpis.totalSemDesconto / casasVendidas.count : 0;
  const precoMedioPreChave =
    kpis && casasVendidas.count > 0 ? kpis.preChave / casasVendidas.count : 0;
  const casasSubtitle =
    casasVendidas.source === "units"
      ? "Unidades distintas nas vendas (bulk sales)"
      : "Contratos sem vínculo de unidade no retorno";

  const hasCharts = data != null || loading;

  return (
    <div className="flex flex-col gap-6">

      {/* ── Cabeçalho ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Comercial</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Vendas (Sienge bulk-data) no período filtrado
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={apply} loading={loading}
          leftIcon={<RefreshCw className="w-4 h-4" />}>
          Atualizar
        </Button>
      </div>

      {/* ── Filtros ── */}
      <FilterBar
        values={values} onChange={setValues} onApply={apply} onReset={reset}
        costCenterOptions={ccOptions} loadingOptions={loadingCC} loading={loading}
      />

      {/* ── Erro ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={apply} className="ml-auto text-red-500 hover:text-red-700 underline text-xs">
            Tentar novamente
          </button>
        </div>
      )}

      {/* ── KPI Cards (uma linha; rolagem horizontal em telas estreitas) ── */}
      <div className="w-full overflow-x-auto pb-1">
        <div className="flex flex-nowrap gap-4 min-w-[90rem] sm:min-w-0">
          <Card
            title="Preço Geral"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatCurrency(kpis?.totalSemDesconto ?? 0)}
            subtitle="Total de vendas válidas sem DE no período"
            icon={<Banknote className="w-5 h-5" />}
            loading={isFirstLoad}
            className="border-l-4 border-l-amber-400 flex-1 min-w-[14.5rem] shrink-0"
          />
          <Card
            title="Preço Médio Geral"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatCurrency(precoMedioGeral)}
            subtitle="Total de vendas sem DE ÷ casas vendidas"
            icon={<TrendingUp className="w-5 h-5" />}
            loading={isFirstLoad}
            className="border-l-4 border-l-purple-400 flex-1 min-w-[14.5rem] shrink-0"
          />
          <Card
            title="Preço Médio Pré-Chave"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatCurrency(precoMedioPreChave)}
            subtitle="Condições exceto PP/PF/DE ÷ casas vendidas"
            icon={<CircleDollarSign className="w-5 h-5" />}
            loading={isFirstLoad}
            className="border-l-4 border-l-emerald-400 flex-1 min-w-[14.5rem] shrink-0"
          />
          <Card
            title="Casas vendidas"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatNumber(casasVendidas.count)}
            subtitle={casasSubtitle}
            icon={<Home className="w-5 h-5" />}
            loading={isFirstLoad}
            onClick={data != null && !loading ? () => setCasasModalOpen(true) : undefined}
            className="border-l-4 border-l-sky-400 flex-1 min-w-[14.5rem] shrink-0"
          />
          <Card
            title="Velocidade de Vendas"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatNumber(velocidadeVendas.value, 2)}
            subtitle={
              velocidadeVendas.estoqueAberto
                ? "Até a data da pesquisa, incluindo meses sem vendas"
                : "Até a última venda, incluindo meses sem vendas"
            }
            icon={<Gauge className="w-5 h-5" />}
            loading={isFirstLoad}
            onClick={data != null && !loading ? () => setVelocidadeModalOpen(true) : undefined}
            className="border-l-4 border-l-indigo-400 flex-1 min-w-[14.5rem] shrink-0"
          />
          <Card
            title="Distratos"
            value={awaitingApply ? undefined : isFirstLoad ? undefined : formatNumber(distratosCount)}
            subtitle="Contratos cancelados no período (data de distrato)"
            icon={<FileX className="w-5 h-5" />}
            loading={isFirstLoad}
            onClick={data != null && !loading ? () => setDistratosModalOpen(true) : undefined}
            className="border-l-4 border-l-rose-400 flex-1 min-w-[14.5rem] shrink-0"
          />
        </div>
      </div>

      {awaitingApply && (
        <p className="text-sm text-slate-500 text-center py-2">
          Defina o período, os centros de custo (opcional) e clique em <strong>Aplicar</strong> para carregar os dados.
        </p>
      )}

      {isFirstLoad && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500">Consultando o Sienge… isso pode levar alguns segundos.</p>
        </div>
      )}

      {/* ══════════════════ SEÇÃO: ANÁLISE DE RECEITAS ══════════════════ */}
      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Análise de vendas
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Linha 1: Vendas por mês + evolução líquida */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
            <ReceitasPorMes data={receitasPorMes} loading={isFirstLoad} />
            <EvolucaoVendas
              data={evolucaoVendas}
              metaUnidades={units.length}
              loading={isFirstLoad}
            />
          </div>

          {/* ══════════════════ SEÇÃO: VENDEDORES ══════════════════ */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Vendedores
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Ranking de Vendedores/Imobiliárias */}
          <div>
            <RankingClientes
              data={brokers}
              loading={isFirstLoad}
              title="Ranking de Vendedores"
              subtitle="Top 10 corretores / imobiliárias (brokers)"
              onExpand={() => setVendedoresModalOpen(true)}
              showQuantity
              detailKind="client"
            />
          </div>
        </>
      )}

      <CasasVendidasModal
        open={casasModalOpen}
        onClose={() => setCasasModalOpen(false)}
        rows={casasDetalhe}
      />
      <DistratosModal
        open={distratosModalOpen}
        onClose={() => setDistratosModalOpen(false)}
        rows={distratosDetalhe}
      />
      <VelocidadeVendasModal
        open={velocidadeModalOpen}
        onClose={() => setVelocidadeModalOpen(false)}
        rows={velocidadeMensal}
        velocidade={velocidadeVendas.value}
      />
      <VendedoresRankingModal
        open={vendedoresModalOpen}
        onClose={() => setVendedoresModalOpen(false)}
        rows={allBrokers}
      />
    </div>
  );
}
